import { visit } from "unist-util-visit";
import type { Root, Text, Parent } from "mdast";

/**
 * Task 5.11 — turns literal "[1]" citation markers into `<sup data-n="1">`
 * chip nodes, so Markdown.tsx's `components.sup` can render CitationChip.
 *
 * There is no `components.text` override in react-markdown v6+ (removed in
 * 6.0.0, never reintroduced in v7-v9), so this has to run as a remark
 * plugin operating on the mdast tree instead of a post-render string pass.
 * Operating on the parsed tree gives two correctness properties for free
 * that a naive string-replace on the raw markdown would not:
 *
 *   - `[1]` inside a fenced or inline code block is never touched — code
 *     nodes have type "code"/"inlineCode", never "text", so `visit(tree,
 *     "text", ...)` never sees their contents.
 *   - `[text](url)` markdown links are never mistaken for citations — by
 *     the time this plugin runs, `[label](url)` has already been parsed
 *     into a `link` node with its own "text" child ("label"), which does
 *     not itself look like `[\d+]`. This plugin only ever matches text
 *     nodes containing a literal, unlinked "[<digits>]".
 */
const CITE_RE = /\[(\d+)\]/g;

function citationNode(n: string): any {
  return {
    type: "citationChip",
    data: { hName: "sup", hProperties: { className: "cite", "data-n": n } },
    children: [{ type: "text", value: n }],
  };
}

export default function remarkCitations() {
  return (tree: Root) => {
    visit(tree, "text", (node: Text, index, parent: Parent | undefined | null) => {
      if (!parent || index === undefined || index === null) return;

      const value = node.value;
      CITE_RE.lastIndex = 0;
      if (!CITE_RE.test(value)) return;
      CITE_RE.lastIndex = 0;

      const newNodes: any[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      while ((match = CITE_RE.exec(value)) !== null) {
        if (match.index > lastIndex) {
          newNodes.push({ type: "text", value: value.slice(lastIndex, match.index) });
        }
        newNodes.push(citationNode(match[1]));
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < value.length) {
        newNodes.push({ type: "text", value: value.slice(lastIndex) });
      }

      parent.children.splice(index, 1, ...newNodes);
      return index + newNodes.length;
    });
  };
}
